/**
 * use-admin-global-search — hook globalnej wyszukiwarki panelu ITE (super admin).
 *
 * Woła `GET /api/admin/search?q=` (sesja cookie). Odpowiednik tenantowego
 * `use-global-search`. Grupy z bazy (klienci/użytkownicy panelu/faktury/kupony/moduły)
 * liczy backend; grupa „Pozycje menu" składana po froncie ze statycznych tras.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface AdminSearchHit {
  id: string;
  title: string;
  subtitle: string | null;
}

export interface AdminGlobalSearchResults {
  tenants: AdminSearchHit[];
  adminUsers: AdminSearchHit[];
  invoices: AdminSearchHit[];
  coupons: AdminSearchHit[];
  modules: AdminSearchHit[];
}

export interface AdminGlobalSearchResponse {
  q: string;
  results: AdminGlobalSearchResults;
  total: number;
}

/** Minimalna długość frazy — spójna z backendem. */
export const ADMIN_MIN_SEARCH_LEN = 2;

export function useAdminGlobalSearch(query: string, opts?: { enabled?: boolean }) {
  const term = query.trim();
  const enabled = (opts?.enabled ?? true) && term.length >= ADMIN_MIN_SEARCH_LEN;
  return useQuery<AdminGlobalSearchResponse>({
    queryKey: ['admin', 'global-search', term],
    queryFn: async () => {
      const res = await api.get<AdminGlobalSearchResponse>('/admin/search', {
        params: { q: term },
      });
      return res.data;
    },
    enabled,
    staleTime: 15_000,
  });
}
